import React, { useEffect, useState, useContext, useRef } from 'react';
import {
  SuperList,
  DisabledTag,
  getTranslatedText,
  ALL_TABLES,
  getDefaultValue,
  useSuperListHook,
  CurrencyText
} from '@nile/components';
import DateTimeCell from '@nile/mangifera/src/Components/Ticket/DateTimeCell';
import { AccountClient, GET_CLIENTS } from '@nile/graphql';
import {
  TextStyle,
  Link,
  DisplayText,
  Button
} from '@superops/ui-design-system';
import { FormattedMessage, useIntl } from 'react-intl';
import envConfig from '@nile/home/src/Config/env';

import { GET_CLIENT_INVOICE_STATEMENT_LIST } from '@nile/reports/src/modules/InvoiceStatements/invoiceGql';
import ReportsContext from '@nile/reports/src/modules/home/contexts/Reports';
import NewReportShare from '../reports/components/subpages/NewReportShare';
import { useInvoiceStatementsStore } from '@nile/reports/src/modules/InvoiceStatements/invoiceStatementsStore';

const DEFAULT_ROW_COUNT = 20;

const defaultCondition = {
  joinOperator: 'AND',
  operands: []
};

interface InvoiceStatementsTableProps {
  reportId: string;
  onViewStatement: (client: { accountId: string; name: string }) => void;
}

const InvoiceStatementsTable = ({
  reportId,
  onViewStatement
}: InvoiceStatementsTableProps) => {
  const intl = useIntl();

  const {
    records,
    setRecords,
    tableCondition,
    updateTableFilterCondition,
    showResetFilter,
    resetFilters,
    updateAdvanceFilterCondition,
    advanceFilterCondition
  } = useSuperListHook({
    tableName: ALL_TABLES.invoiceStatementsTable,
    viewId: reportId
  });

  const { reportAllowedDateRange, client } = useInvoiceStatementsStore();

  const onViewStatementRef = useRef(onViewStatement);
  onViewStatementRef.current = onViewStatement;

  useEffect(() => {
    updateAdvanceFilterCondition({
      joinOperator: 'AND',
      operands: reportAllowedDateRange ? [reportAllowedDateRange] : []
    });
  }, [reportAllowedDateRange]);

  const [data, setData] = useState([]);
  const reportsContext = useContext(ReportsContext);
  const [sheetOpen, setSheetOpen] = useState({
    value: false,
    content: null
  });

  const columns = [
    {
      Header: getTranslatedText(intl, 'common.client'),
      accessor: 'client.name'
    },
    {
      Header: getTranslatedText(intl, 'invoice.statements.totalOutstanding'),
      accessor: 'outstandingAmount'
    },
    {
      Header: getTranslatedText(intl, 'invoice.statements.credits'),
      accessor: 'creditsAvailable'
    },
    {
      Header: getTranslatedText(intl, 'invoice.statements.unpaidInvoices'),
      accessor: 'unpaidInvoices'
    },
    {
      Header: getTranslatedText(intl, 'invoice.statements.lastPayment'),
      accessor: 'lastPaymentReceived'
    }
  ];

  const generateOptions = (col: any, clientData: any) => {
    switch (col?.name) {
      case 'client.name':
        return clientData?.getClientList?.clients?.map((el: any) => {
          return {
            ...el,
            name: el.accountId,
            value: el.name,
            label: el.name
          };
        });
      default:
        return null;
    }
  };

  const getDynamicQuery = (col: any) => {
    switch (col?.name) {
      case 'client.name':
        return AccountClient.query({
          query: GET_CLIENTS,
          variables: {
            listInfo: {
              sort: {
                attribute: 'name',
                order: 'ASC'
              }
            }
          }
        });
      default:
        return null;
    }
  };

  const checkDynamicQuery = (col: any) => {
    switch (col?.name) {
      case 'client.name':
        return true;
      default:
        return false;
    }
  };

  const getFilterType = (column: any) => {
    switch (column.accessor) {
      case 'client.name':
        return 'select';
      default:
        return 'text';
    }
  };

  const getDataType = (column: any) => {
    switch (column.accessor) {
      case 'outstandingAmount':
      case 'creditsAvailable':
      case 'unpaidInvoices':
        return 'number';
      case 'lastPaymentReceived':
        return 'date';
      default:
        return 'string';
    }
  };

  const [columnGroup, setColumnGroup] = useState<any[]>([]);

  const CurrencyCell = ({ cell }: { cell: any }) => {
    return cell?.value !== undefined && cell?.value !== null ? (
      <CurrencyText value={Number(cell?.value)} />
    ) : (
      <DisabledTag>
        <FormattedMessage id="app.noData.message" />
      </DisabledTag>
    );
  };

  const DynamicCell = ({ cell }: { cell: any }) => {
    const { actions } = cell?.column;
    const rowData = cell?.row?.original;

    switch (cell.column.accessor) {
      case 'action':
        return (
          <Link
            onClick={() => actions.onClick(rowData)}
            className="cursor-pointer underline f-12"
            role="button"
            tabIndex={cell?.row?.index || 0}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
          >
            <FormattedMessage id="invoice.statements.viewStatement" />
          </Link>
        );
      case 'outstandingAmount':
      case 'creditsAvailable':
        return <CurrencyText value={Number(cell?.value ?? 0)} />;
      default:
        return cell?.value ? (
          <div>{cell?.value}</div>
        ) : (
          <DisabledTag>
            <FormattedMessage id="app.noData.message" />
          </DisabledTag>
        );
    }
  };

  const defaultColumnCells = {
    DynamicCell: DynamicCell,
    Currency: CurrencyCell,
    DateTime: DateTimeCell
  };

  const getActions = () => {
    return {
      onClick: (item: any) => {
        if (onViewStatementRef.current) {
          onViewStatementRef.current(item.client);
        }
      }
    };
  };

  function getSortBy(column: any) {
    switch (column.accessor) {
      case 'client.name':
      case 'outstandingAmount':
      case 'unpaidInvoices':
        return true;
      case 'creditsAvailable':
      case 'lastPaymentReceived':
        return false;
      default:
        return false;
    }
  }

  function getFilterBy(column: any) {
    switch (column.accessor) {
      case 'client.name':
        return true;
      default:
        return false;
    }
  }

  function getCellType(column: any) {
    switch (column.accessor) {
      case 'outstandingAmount':
      case 'creditsAvailable':
        return 'Currency';
      case 'lastPaymentReceived':
        return 'DateTime';
      default:
        return 'DynamicCell';
    }
  }

  function generateColumns(): void {
    const columnsGrp = columns.map((col) => {
      return {
        name: col.accessor,
        Header: col.Header,
        header: col.Header,
        accessor: col.accessor,
        accessorKey: col.accessor,
        id: col.accessor,
        size: 100,
        disableResizing: false,
        disableSortBy: !getSortBy(col),
        editable: false,
        disableFilters: !getFilterBy(col),
        filterType: getFilterType(col),
        dataType: getDataType(col),
        type: getCellType(col),
        actions: getActions(),
        field: {
          name: col.accessor,
          accessor: col.accessor,
          label: col.Header,
          sortable: false,
          id: col.accessor,
          dataType: getDataType(col),
          filterType: getFilterType(col)
        },
        helpers: {
          getDefaultValue,
          updateConditions: updateTableFilterCondition,
          checkDynamicQuery,
          getDynamicQuery,
          generateOptions
        }
      };
    });
    columnsGrp.push({
      Header: ' ',
      header: ' ',
      name: ' ',
      accessorKey: 'action',
      accessor: 'action',
      size: 70,
      id: 'action',
      disableResizing: true,
      type: 'DynamicCell',
      disableSortBy: true,
      editable: false,
      disableFilters: true,
      actions: getActions(),
      filterType: '',
      dataType: '',
      field: {
        name: 'action',
        accessor: 'action',
        label: 'Action',
        sortable: false,
        id: 'action',
        dataType: '',
        filterType: ''
      },
      helpers: {
        getDefaultValue,
        updateConditions: updateTableFilterCondition,
        checkDynamicQuery,
        getDynamicQuery,
        generateOptions
      }
    });
    setColumnGroup(columnsGrp);
  }

  useEffect(() => {
    generateColumns();
  }, []);

  function checkBoxDisabledCheck() {
    return false;
  }

  return (
    <>
      {sheetOpen.value && (
        <NewReportShare
          data={reportsContext.activeReport}
          toggleSheetOpen={setSheetOpen}
          sheetOpen={sheetOpen}
          isScheduled={false}
        />
      )}
      <div className="full-width full-height">
        <div className="fx actr jctr position-sticky z400 white-to-grey-bg full-width">
          <div className="fx fw actr mb10 p20 full-width">
            <div>
              <DisplayText size="extraLarge" weight="SemiBold">
                <FormattedMessage id="invoice.statements.title" />
              </DisplayText>
              <TextStyle variation="subdued">
                <FormattedMessage id="invoice.statements.subtitle" />
              </TextStyle>
            </div>
            <div className="mlauto tw-pr-2">
              {showResetFilter && (
                <Button id="resetSuperListFilter" plain onClick={resetFilters}>
                  <FormattedMessage id="ticket.details.resetFilters" />
                </Button>
              )}
            </div>
            <div>
              <Button
                primary
                onClick={() => {
                  setSheetOpen({ value: true, content: 'share' });
                }}
              >
                <FormattedMessage id="invoice.statements.sendStatements" />
              </Button>
            </div>
          </div>
        </div>
        <div
          className={`min-height-300 ${data?.length === 0 ? 'height300' : ''}`}
        >
          <SuperList
            padded="sm"
            data={data}
            headerSticky
            stickyTop={94}
            records={records}
            setRecords={setRecords}
            viewId={reportId}
            setData={setData}
            columns={columnGroup}
            rowCount={data?.length}
            defaultColumnCells={defaultColumnCells}
            defaultRowCount={DEFAULT_ROW_COUNT}
            emptyState={{
              nodataImage: `${envConfig.imageRoot}svg/not-found.svg`,
              heading: getTranslatedText(intl, 'app.general.noData'),
              description: ''
            }}
            defaultSort={[
              {
                attribute: 'client.name',
                order: 'ASC'
              }
            ]}
            isGroupingEnabled={false}
            idKey="client.accountId"
            tableName={ALL_TABLES.invoiceStatementsTable}
            client={AccountClient}
            query={GET_CLIENT_INVOICE_STATEMENT_LIST}
            accessorKey="getClientInvoiceStatementList"
            dataKey="clientInvoiceStatements"
            defaultCondition={defaultCondition}
            tableCondition={tableCondition}
            checkBoxDisabledCheck={checkBoxDisabledCheck}
            advanceFilterCondition={advanceFilterCondition}
          />
        </div>
      </div>
    </>
  );
};

export default InvoiceStatementsTable;
